<template>
  <div>
    <div v-if="screens.data.length">
      <div class="row q-col-gutter-sm justify-between">
        <q-btn-toggle
          v-model="bookingWeek.layoutMode"
          size="sm"
          class="q-my-xs"
          outline
          dense
          toggle-color="primary"
          :options="[
            { label: 'Plan and Actual', value: 'plan-and-actual' },
            { label: 'Plan', value: 'plan' },
            { label: 'Actual', value: 'actual' },
          ]"
        />

        <q-toggle
          v-model="bookingWeek.config.manualLayout"
          label="Manual Layout"
          size="sm"
        />

        <q-checkbox
          v-model="bookingWeek.config.screenTitleOneTwice"
          label="Screen Title One, twice"
          size="sm"
        />
        <q-checkbox
          v-if="bookingWeek.fillTitleCount < screens.length"
          v-model="bookingWeek.config.allowSimultaneousScreenings"
          label="Fill With Simultaneous Screenings"
          size="sm"
        />
        <q-checkbox
          v-if="bookingWeek.fillTitleCount > screens.length"
          v-model="bookingWeek.config.rotateTimesOnSplitScreens"
          label="Rotate Times on Split Screens"
          size="sm"
        />

        <q-btn-toggle
          v-model="displayMode"
          size="sm"
          class="q-my-xs"
          outline
          dense
          toggle-color="primary"
          :options="[
            { label: 'Posters', value: 'poster' },
            { label: 'Name', value: 'title' },
            { label: 'Title #', value: 'number' },
          ]"
        />

        <q-btn-toggle
          v-model="colorBy"
          size="sm"
          class="q-my-xs"
          outline
          dense
          toggle-color="primary"
          :options="[
            { label: 'Plan Element', value: 'planElement' },
            { label: 'Movie', value: 'movie' }
          ]"
        />

        <q-btn-toggle
          v-model="by"
          size="sm"
          class="q-my-xs"
          outline
          dense
          toggle-color="primary"
          :options="[
            { label: 'By Day', value: 'day' },
            { label: 'By Screen', value: 'screen' }
          ]"
        />
      </div>

      <div
        v-if="bookingWeek.config.manualLayout"
        class="row q-gutter-sm q-mb-lg"
      >
        <q-btn-group
          outline
          dense
        >
          <template v-for="planElement in bookingWeek.planElements.data">
            <q-btn
              v-for="titleNumber in planElement.numbers"
              :key="'p-' + planElement.id + '-' + titleNumber"
              dense
              size="sm"
              class="q-my-xs"
              outline
              :class="{ 'selected-button': paintValue === planElement.bookingWeekMovies.data[titleNumber - 1] }"
              @click="paintValue = planElement.bookingWeekMovies.data[titleNumber - 1]"
            >
              <layout-slot
                :layout-slot="{
                  planElement,
                  titleNumber,
                  bookingWeekMovie: planElement.bookingWeekMovies.data[titleNumber - 1],
                  movie: planElement.bookingWeekMovies.data[titleNumber - 1].movie
                }"
                :display-mode="displayMode"
                :color-by="colorBy"
                size="sm"
              />
            </q-btn>
          </template>

          <q-btn
            dense
            size="sm"
            class="q-my-xs"
            outline
            :class="{ 'selected-button': paintValue === null }"
            @click="paintValue = null"
          >
            Clear
          </q-btn>
          <q-btn
            dense
            size="sm"
            class="q-my-xs"
            outline
            :class="{ 'selected-button': paintValue === 'default' }"
            @click="paintValue = 'default'"
          >
            <div class="row no-wrap justify-center q-gutter-sm items-center">
              <div
                class="col-auto title-count"
                :style="'color: #444; border-color: #444; background-color: #44433;'"
              >
                #
              </div>
              <div>
                Magic
              </div>
            </div>
          </q-btn>
        </q-btn-group>
      </div>
      <div
        v-if="by==='screen'"
        class="row q-gutter-xl justify-center"
      >
        <div
          v-for="screen in screens.data"
          :key="screen.id"
          class="screen"
        >
          <div class="text-overline text-dark text-bold">
            {{ screen.name }}
          </div>
          <table class="non-selectable">
            <thead>
              <tr>
                <th
                  :style="{ 'cursor': editable ? 'se-resize' : '' }"
                  @mousedown="editable && toggle(screen.id)"
                  @mouseover="(event) => editable && toggle(screen.id, null, null, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                />
                <th
                  v-for="timeSlot in timeSlots.data"
                  :key="timeSlot.id"
                  :style="{ 'cursor': editable ? 's-resize' : '' }"
                  @mousedown="editable && toggle(screen.id, null, timeSlot.id)"
                  @mouseover="(event) => editable && toggle(screen.id, null, timeSlot.id, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                >
                  {{ timeSlot.shortName }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="dayNumber in currentCircuit.daysOfWeekNumbers"
                :key="dayNumber"
              >
                <td
                  class="th"
                  style="width: 50px"
                  :style="{ 'cursor': editable ? 'e-resize' : '' }"
                  :class="{ poster: displayMode === 'poster' }"
                  @mousedown="editable && toggle(screen.id, dayNumber, null)"
                  @mouseover="(event) => editable && toggle(screen.id, dayNumber, null, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                >
                  {{ currentCircuit.daysOfWeek[currentCircuit.daysOfWeekNumbers.indexOf(dayNumber)] }}
                </td>
                <td
                  v-for="timeSlot in timeSlots.data"
                  :key="timeSlot.id"
                  :set="showingSlot = layoutSlot(screen.id, timeSlot.id, dayNumber)"
                  class="text-center"
                  :style="{ 'cursor': editable ? 'cell' : '' }"
                  :class="{ 'closed-time': (!isShowtimeOpen(timeSlot.id, dayNumber)) }"
                  @mousedown="editable && toggle(screen.id, dayNumber, timeSlot.id)"
                  @mouseover="(event) => editable && toggle(screen.id, dayNumber, timeSlot.id, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                >
                  <layout-slot
                    :layout-slot="showingSlot"
                    :display-mode="displayMode"
                    :color-by="colorBy"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-if="by==='day'"
        class="row q-gutter-xl justify-center"
      >
        <div
          v-for="(dayNumber, dayIndex) in currentCircuit.daysOfWeekNumbers"
          :key="dayNumber"
          class="screen"
        >
          <div class="text-overline text-dark text-bold">
            {{ currentCircuit.daysOfWeekFull[dayIndex] }}
          </div>
          <table class="non-selectable">
            <thead>
              <tr>
                <th
                  :style="{ 'cursor': editable ? 'se-resize' : '' }"
                  @mousedown="editable && toggle(null, dayNumber)"
                  @mouseover="(event) => editable && toggle(null, dayNumber, null, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                />
                <th
                  v-for="timeSlot in timeSlots.data"
                  :key="timeSlot.id"
                  :style="{ 'cursor': editable ? 's-resize' : '' }"
                  @mousedown="editable && toggle(null, dayNumber, timeSlot.id)"
                  @mouseover="(event) => editable && toggle(null, dayNumber, timeSlot.id, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                >
                  {{ timeSlot.shortName }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="screen in screens.data"
                :key="screen.id"
              >
                <td
                  class="th"
                  :class="{ poster: displayMode === 'poster' }"
                  style="width: 50px"
                  :style="{ 'cursor': editable ? 'e-resize' : '' }"
                  @mousedown="editable && toggle(screen.id, dayNumber)"
                  @mouseover="(event) => editable && toggle(screen.id, dayNumber, null, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                >
                  {{ screen.displayOrder }}
                </td>
                <td
                  v-for="timeSlot in timeSlots.data"
                  :key="timeSlot.id"
                  :set="showingSlot = layoutSlot(screen.id, timeSlot.id, dayNumber)"
                  class="text-center"
                  :style="{ 'cursor': editable ? 'cell' : '' }"
                  :class="{ 'closed-time': (!isShowtimeOpen(timeSlot.id, dayNumber) && !layoutSlot(screen.id, timeSlot.id, dayNumber)) }"
                  @mousedown="editable && toggle(screen.id, dayNumber, timeSlot.id)"
                  @mouseover="(event) => editable && toggle(screen.id, dayNumber, timeSlot.id, event)"
                  @mouseout="bookingWeek.previewingAction = false"
                >
                  <layout-slot
                    :layout-slot="showingSlot"
                    :display-mode="displayMode"
                    :color-by="colorBy"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { showingSlotKey } from 'src/models/schedule/BookingWeek'
import ShowingSlot from 'src/models/schedule/ShowingSlot'
import LayoutSlot from 'src/modules/admin/booking-week/components/LayoutSlot'

export default {
  name: 'CombindedLayout',
  components: {
    LayoutSlot
  },
  props: {
    bookingWeek: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      paintValue: null,
      by: 'day', // 'screen', 'day'
      displayMode: 'poster',
      colorBy: 'planElement',
      toggleValue: null
    }
  },
  configuration: ['currentCircuit', 'timeSlots', 'screens'],
  computed: {
    currentCircuitSchedule () {
      return this.currentCircuit.currentCircuitSchedule
    },
    editable () {
      return this.bookingWeek.config.manualLayout
    }
  },
  methods: {
    isShowtimeOpen (timeSlotId, dayNumber) {
      return this.bookingWeek.isShowtimeOpen(timeSlotId, dayNumber)
    },
    toggle (screenIdFilter, dayNumberFilter, timeSlotIdFilter, event) {
      // Preview or actual
      let layout

      const dragging = event && event.buttons
      this.bookingWeek.previewingAction = event && !event.buttons

      if (this.bookingWeek.previewingAction) {
        // Clone only the hashes and not the models
        layout = _.clone(this.bookingWeek.customLayoutMap)
      } else {
        layout = this.bookingWeek.customLayoutMap
      }

      if (!dragging) {
        this.toggleValue = undefined
      }

      _.each(this.screens.data, (screen) => {
        if (screenIdFilter && screen.id !== screenIdFilter) {
          return
        }
        _.each(this.timeSlots.data, (timeSlot) => {
          if (timeSlotIdFilter && timeSlot.id !== timeSlotIdFilter) {
            return
          }
          _.each(this.currentCircuit.daysOfWeekNumbers, (dayNumber) => {
            if (dayNumberFilter !== null && dayNumberFilter !== undefined && dayNumber !== dayNumberFilter) {
              return
            }

            // If doing any type of bulk set, don't change things outside of the schedule
            // Allow bulk set of closed showtimes if painting by magic or clearing
            if (this.paintValue !== 'default' && this.paintValue !== null && (!screenIdFilter || !timeSlotIdFilter || dayNumberFilter === null || dayNumberFilter === undefined) && !this.isShowtimeOpen(timeSlot.id, dayNumber)) {
              return
            }

            const key = showingSlotKey(screen.id, timeSlot.id, dayNumber)
            const customValue = this.bookingWeek.customLayoutMap[key]?.bookingWeekMovie

            // Whats the value
            let finalValue

            if (this.paintValue === 'default') {
              finalValue = this.bookingWeek.computedLayout[key]?.bookingWeekMovie
            } else if (this.toggleValue !== undefined) {
              finalValue = this.toggleValue
            } else {
              // We are setting the toggle value for this and any upcoming drag events
              this.toggleValue = this.paintValue

              // If the paintValue is already what we are attempting to paint, toggle to unselected
              if (
                customValue &&
                this.paintValue !== 'default' && this.paintValue !== null && customValue &&
                customValue.priorityOrder === this.paintValue.priorityOrder &&
                customValue.planElement.id === this.paintValue.planElement.id) {
                this.toggleValue = null
              }

              finalValue = this.toggleValue
            }

            if (!this.bookingWeek.previewingAction) {
              if (!finalValue) {
                delete layout[key]
                return
              }
              if (finalValue === customValue) {
                return
              }

              const newShowingSlot = new ShowingSlot({
                timeSlotId: timeSlot.id,
                screenId: screen.id,
                dayNumber,

                bookingWeekMovie: finalValue,

                bookingWeek: this.bookingWeek

              })

              layout[newShowingSlot.layoutKey] = newShowingSlot
            } else {
              // Do things a little different if previewing
              if (finalValue === customValue) {
                return
              }

              if (!finalValue && !customValue) {
                delete layout[key]
                return
              }

              // If we are previewing adding or removing, we want to show a value with an indicator that it's a preview
              // e.g. if we are deleting something, we want to show the old value, so not always the preview value
              if (finalValue !== customValue) {
                finalValue = (finalValue || customValue)
              }

              const newShowingSlot = new ShowingSlot({
                timeSlotId: timeSlot.id,
                screenId: screen.id,
                dayNumber,

                bookingWeekMovie: finalValue,

                bookingWeek: this.bookingWeek,

                // This will only be true if we are previewing and will only get used if the value is different from the current value
                preview: true
              })

              layout[newShowingSlot.layoutKey] = newShowingSlot
            }
          })
        })
      })

      // Trigger change
      if (this.bookingWeek.previewingAction) {
        this.bookingWeek.previewLayout = null
        this.bookingWeek.previewLayout = layout
      } else {
        this.bookingWeek.customLayoutMap = null
        this.bookingWeek.customLayoutMap = layout
      }
    },
    layoutSlot (screenId, timeSlotId, dayNumber) {
      const key = showingSlotKey(screenId, timeSlotId, dayNumber)
      return this.bookingWeek.displayLayout[key]
    },
    setAll (value, event) {
      // Preview or actual
      let layout
      if (event && !event.buttons) {
        this.bookingWeek.previewingAction = true
        layout = _.clone(this.bookingWeek.customLayoutMap)
      } else {
        layout = this.bookingWeek.customLayoutMap
        this.bookingWeek.previewingAction = false
      }

      _.each(this.screens.data, (screen) => {
        _.each(this.timeSlots.data, (timeSlot) => {
          _.each(this.currentCircuit.daysOfWeekNumbers, (dayNumber) => {
            let finalValue

            const key = showingSlotKey(screen.id, timeSlot.id, dayNumber)
            const customValue = this.bookingWeek.customLayoutMap[key]?.bookingWeekMovie

            if (value === 'default') {
              finalValue = customValue
            } else {
              if (!this.isShowtimeOpen(timeSlot.id, dayNumber)) {
                finalValue = null
              } else {
                finalValue = value
              }
            }

            if (!this.bookingWeek.previewingAction) {
              if (!finalValue) {
                delete layout[key]
                return
              }
              if (finalValue === customValue) {
                return
              }

              const newShowingSlot = new ShowingSlot({
                timeSlotId: timeSlot.id,
                screenId: screen.id,
                dayNumber,

                bookingWeekMovie: finalValue,

                bookingWeek: this.bookingWeek

              })

              layout[newShowingSlot.layoutKey] = newShowingSlot
            } else {
              // Do things a little different if previewing
              if (finalValue === customValue) {
                return
              }

              if (!finalValue && !customValue) {
                delete layout[key]
                return
              }

              // If we are previewing adding or removing, we want to show a value with an indicator that it's a preview
              // e.g. if we are deleting something, we want to show the old value, so not always the preview value
              if (finalValue !== customValue) {
                finalValue = (finalValue || customValue)
              }

              const newShowingSlot = new ShowingSlot({
                timeSlotId: timeSlot.id,
                screenId: screen.id,
                dayNumber,

                bookingWeekMovie: finalValue,

                bookingWeek: this.bookingWeek,

                // This will only be true if we are previewing and will only get used if the value is different from the current value
                preview: true
              })

              layout[newShowingSlot.layoutKey] = newShowingSlot
            }
          })
        })
      })
      // Trigger change
      if (this.bookingWeek.previewingAction) {
        this.bookingWeek.previewLayout = null
        this.bookingWeek.previewLayout = layout
      } else {
        this.bookingWeek.customLayoutMap = null
        this.bookingWeek.customLayoutMap = layout
      }
    },
    setPaintValue (paintValue) {
      this.paintValue = paintValue
    }
  }
}

</script>

<style lang="scss" scoped>
  .closed-time {
    background-color: #CCC;
  }

  .preview {
   background-color: #DEDEDE;
  }

  .screen {
    display: inline-block;
  }

  .title-count {
    display: inline-block;
    text-align: center;
    width: 20px;
    height: 20px;
    font-size: 13px;
    line-height: 20px;
    border-radius: 100px;
    border: solid 1px;
  }

  table {
      border: 1px solid #ddd;
      border-collapse: separate;
      border-left: 0;
      border-radius: 4px;
      border-spacing: 0px;
  }
  thead {
      display: table-header-group;
      vertical-align: middle;
      border-color: inherit;
      border-collapse: separate;
  }
  tr {
      display: table-row;
      vertical-align: inherit;
      border-color: inherit;
  }
  th, td {
      padding: 5px 4px 6px 4px;
      text-align: center;
      vertical-align: top;
      border-left: 1px solid #ddd;
  }
  td {
      border-top: 1px solid #ddd;
      padding: 0;
  }
  td.th {
    text-align: right;
    padding-right: 6px;
  }

  th, td.th {
    font-weight: 500;
  }

  td
  thead:first-child tr:first-child th:first-child, tbody:first-child tr:first-child td:first-child {
      border-radius: 4px 0 0 0;
  }
  thead:last-child tr:last-child th:first-child, tbody:last-child tr:last-child td:first-child {
      border-radius: 0 0 0 4px;
  }
  td {
    width: 50px;
    height: 27px;
    vertical-align: middle;
  }
  td.poster {
    height: 72px;
  }
  td.poster-present {
    border-width: 2px;
  }

  .poster-img {
    width: 45px;
    height: 67px;
  }

  .selected-button {
    background-color: #DDD !important;
  }

</style>
